import React, { useContext } from 'react';
import './styles.scss';
import { addOverlay, OverlayEnum, SspProviderContext } from '../../did-utility';
import SSPContentVO from 'sspcontentext/src/model/SSPContentVO';
import Disclaimers from '../../did-utility/src/Components/Disclaimers';
import {
	ConsumptionElementType,
	consumptionList,
	ConsumptionListType,
	disclaimerTagsInConsumptionLists,
	getBatteryValue,
	getConsumptionValue,
} from '../../did-utility/src/Functions/consumptionLists';
import i18next from 'i18next';
import { engineTypes } from '../../did-utility/src/Components/Label/dataLabel';
import { getSetup } from '../../did-utility/src/Functions/getSetup';

export default function TechData(): JSX.Element {
	const store = useContext(SspProviderContext);
	const setup = getSetup();
	const contentModel = store.state.content
		? store.state.content.asModel
		: new SSPContentVO('').asModel;
	const vehicleDataModel = contentModel.ref('vehicle_data').asModel;
	const engine = contentModel.ref('engine').asModel;
	const engineType = engineTypes.includes(
		engine.str('engine_type', 'Default')
	)
		? (engine.str('engine_type', 'Default') as keyof ConsumptionListType)
		: ('Default' as keyof ConsumptionListType);
	const techEntries = contentModel.ref('engine').asModel.reflist('entries');
	const tyreLabels = contentModel
		.ref('vehicle_data')
		.asModel.reflist('tyre_labels');
	const hasWLTPData =
		vehicleDataModel.keys().some(key => key.includes('wltp')) ||
		(setup.market.name === 'MEX' &&
			vehicleDataModel
				.keys()
				.some(
					key =>
						key.includes('City') ||
						key.includes('Highway') ||
						key.includes('Observed')
				));
	let tyreString = '';

	tyreLabels.forEach((label, index) => {
		tyreString +=
			label.asModel.str('type') +
			' ' +
			label.asModel.str('tyreDesignation') +
			(index === tyreLabels.length - 1 ? '' : ', ');
	});

	const engineDisclaimers = engine.arr('disclaimers') as Array<{
		Key: string;
		Value: string;
	}>;
	const disclaimerTags: Array<string> = [
		...disclaimerTagsInConsumptionLists(engineType),
	];

	techEntries.forEach(entry => {
		const entriesArray = entry.asModel.arr('entries') as Array<
			Record<string, Array<{ Key: string; Value: string }>>
		>;

		entriesArray.forEach(entryObj => {
			Object.values(entryObj).forEach(langEntries => {
				langEntries.forEach(e => {
					const tag = e.Key.substring(
						e.Key.indexOf('#') + 1,
						e.Key.lastIndexOf('#')
					);
					const index = engineDisclaimers.findIndex(
						dis => dis.Key === tag
					);
					if (tag.length > 0 && index !== -1) {
						disclaimerTags.push(tag);
					}
				});
			});
		});
	});

	const getConsumption = (c: ConsumptionElementType) => {
		const disclaimerNumber =
			c.disclaimer && disclaimerTags.includes(c.disclaimer)
				? engineDisclaimers.findIndex(d => d.Key === c.disclaimer)
				: -1;
		if (c.hideIfEmpty && !vehicleDataModel.has(c.key)) {
			return <></>;
		}

		if (c.subtitle) {
			return (
				<div
					className='table-element headline tech-data'
					key={`consumption-subtitle-${c.key}`}
				>
					<h6 className='subtitle bold'>{i18next.t(c.headline)}</h6>
				</div>
			);
		}

		return (
			<div
				className={`table-element tech-data ${getBatteryValue(c, engine) === '' || getConsumptionValue(c, vehicleDataModel) === '' ? 'empty' : ''}`}
				key={`tech-data-consumption-element-${c.key}`}
			>
				<h6 className='start'>
					{i18next.t(c.headline)}
					{disclaimerNumber !== -1 && (
						<sup>{disclaimerNumber + 1}</sup>
					)}
				</h6>
				<p className='short middle with-end'>
					{c.engine
						? getBatteryValue(c, engine)
						: getConsumptionValue(c, vehicleDataModel)}
				</p>
			</div>
		);
	};

	return (
		<>
			{tyreString && (
				<>
					<div className='table-element headline tech-data'>
						<h6 className='bold'>
							{i18next.t('TireLabelInformation')}
						</h6>
					</div>
					<div
						className='table-element tech-data'
						onClick={() => addOverlay(store, OverlayEnum.Tire)}
					>
						<h6 className='start'>
							{i18next.t('TireLabelMainTires')}
						</h6>
						<p className='short middle with-end ellipsis'>
							{tyreString}
						</p>
						<div className='info-icon'>
							<svg>
								<use xlinkHref='#info-icon' />
							</svg>
						</div>
					</div>
				</>
			)}
			{hasWLTPData && (
				<>
					<div className='table-element headline tech-data'>
						<h6 className='bold'>
							{i18next.t('WltpValuesTableHeadline')}
						</h6>
					</div>
					{consumptionList.wltp &&
						consumptionList.wltp[engineType]?.map((item, index) => (
							<React.Fragment key={item.key}>
								{getConsumption(item)}
							</React.Fragment>
						))}
				</>
			)}
			{techEntries.map((entry, index) => {
				let entryList = entry.asModel.target.entries[
					i18next.language
				] as Array<{
					Key: string;
					Value: string;
				}>;

				let entryTitle = entry.asModel.target.name[i18next.language];

				return (
					<React.Fragment key={`entry-${index}`}>
						<div
							className={`table-element headline tech-data ${
								entryList.length < 1 ||
								(entryList.length === 1 &&
									entryList[0].Key === '')
									? 'empty'
									: ''
							}`}
						>
							<h6 className='bold'>{entryTitle}</h6>
						</div>
						{entryList.map((e, index) => {
							let key = (
								<h6
									className={`start ${
										e.Key === '' ? 'empty' : ''
									}`}
								>
									{e.Key}
								</h6>
							);
							engineDisclaimers.forEach((disclaimer, index) => {
								if (e.Key.includes(disclaimer.Key)) {
									key = (
										<h6 className='start'>
											{e.Key.replace(
												'#' + disclaimer.Key + '#',
												''
											)}
											<sup>{index + 1}</sup>
										</h6>
									);
								}
							});
							return (
								<div
									className='table-element tech-data'
									key={`entry-e-${index}`}
								>
									{key}
									<p className='short'>{e.Value}</p>
								</div>
							);
						})}
					</React.Fragment>
				);
			})}
			{disclaimerTags.length > 0 && engineDisclaimers.length > 0 && (
				<Disclaimers
					tagsUsed={disclaimerTags}
					complete={engineDisclaimers}
				/>
			)}
		</>
	);
}
